"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addGoalMilestone,
  completeMilestone,
  createTask,
  generateSchedule,
  getGoalDriftSummary,
  getGoalAnalysis,
  getGoalStrengthenPlan,
  getWeekShape,
  listGoals,
  undoMilestone,
  updateGoal,
} from "@/lib/api";
import type {
  AutoPlanResponse,
  GoalAnalysis,
  GoalDriftGoal,
  GoalDriftSummary,
  GoalItem,
  TaskItem,
  WeeklyGoalShape,
} from "@/lib/api";
import { getTodayPlacement } from "@/lib/api/today";
import GoalCard from "@/components/GoalCard";
import GoalStudio from "@/components/GoalStudio";
import RoadmapModal from "@/components/RoadmapModal";
import { goalWeightLabel } from "@/lib/goalWeights";
import { resolveGoalProgress } from "@/lib/goalProgress";
import { useDataRefresh, useGoalFocusContext } from "@/lib/store";
import { buildFocusedGoalGroups } from "@/lib/goalFocusView";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";
import { useCrossSurfaceConfirmationStore } from "@/lib/stores/crossSurfaceConfirmation";
import { buildPlacementConfirmation } from "@/components/review/reviewActionHelpers";
import { useChatRevealStore } from "@/lib/stores/chatReveal";
import { useChatRevealHighlight } from "@/lib/useChatRevealHighlight";
import { useOverlayManager } from "@/components/overlays/OverlayManager";

interface Props {
  onNavigate: (tab: string) => void;
}

type MilestoneUndoNotice = {
  goalId: string;
  goalTitle: string;
  milestoneTitle: string;
};

export default function GoalsScreen({ onNavigate }: Props) {
  const showCrossSurfaceConfirmation = useCrossSurfaceConfirmationStore((state) => state.showConfirmation);
  const crossSurfaceConfirmation = useCrossSurfaceConfirmationStore((state) => state.confirmation);
  const activeChatReveal = useChatRevealStore((state) => state.active);
  const goalFocusTarget = useGoalFocusContext((state) => state.target);
  const setGoalFocusTarget = useGoalFocusContext((state) => state.setTarget);
  const clearGoalFocusTarget = useGoalFocusContext((state) => state.clearTarget);
  const [goals, setGoals] = useState<GoalItem[]>([]);
  const [goalDriftSummary, setGoalDriftSummary] = useState<GoalDriftSummary | null>(null);
  // U22 — Layer 1 week shape, keyed by goal_id. Lazy-loaded once goals
  // exist; re-fetched when the user changes a goal's mode / weights /
  // focus_priority so the "This week" line stays honest.
  const [weekShapeByGoal, setWeekShapeByGoal] = useState<
    Record<string, WeeklyGoalShape>
  >({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("active");
  const [goalInsights, setGoalInsights] = useState<Record<string, GoalAnalysis>>({});
  const [activePanels, setActivePanels] = useState<Record<string, "analysis" | "roadmap" | "judge" | null>>({});
  const [loadingGoalId, setLoadingGoalId] = useState<string | null>(null);
  const [goalActionLoading, setGoalActionLoading] = useState<string | null>(null);
  const [goalActionMessage, setGoalActionMessage] = useState<Record<string, string>>({});
  const [roadmapStepActionLoading, setRoadmapStepActionLoading] = useState<string | null>(null);
  const [milestoneUndoNotice, setMilestoneUndoNotice] = useState<MilestoneUndoNotice | null>(null);
  const [showStudio, setShowStudio] = useState(false);
  const [showFullInventory, setShowFullInventory] = useState(false);
  const [roadmapModalGoalId, setRoadmapModalGoalId] = useState<string | null>(null);
  const [goalWeightLoading, setGoalWeightLoading] = useState<string | null>(null);
  const goalCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const lastAutoFocusedGoalId = useRef<string | null>(null);

  const hasLoadedOnceRef = useRef(false);
  const loadGoals = useCallback(async () => {
    if (!hasLoadedOnceRef.current) {
      setLoading(true);
    }
    try {
      const [res, driftSummary] = await Promise.all([
        listGoals(filter === "all" ? undefined : filter),
        filter === "active" ? getGoalDriftSummary().catch(() => null) : Promise.resolve(null),
      ]);
      setGoals(res.goals);
      setGoalDriftSummary(driftSummary);
    } catch {
      // ignore for now
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void loadGoals();
  }, [loadGoals]);

  // Strict UAT 2026-05-24 — Chat Action Contract: when a Chat-driven
  // mutation (create_goal, delete_goal via Undo) fires
  // `notifyGoalChange`, this component must re-fetch so the Goals UI
  // visibly reflects the change. Without this subscription,
  // chat-created goals never appeared in the list and Undo never
  // cleared them from the UI — both P0 failures in the strict UAT.
  const lastGoalChange = useDataRefresh((s) => s.lastGoalChange);
  // BUG-010: completing a milestone here must also notify other surfaces
  // (Progress tab) so they don't stay stale (N3 split-brain).
  const notifyGoalChange = useDataRefresh((s) => s.notifyGoalChange);
  useEffect(() => {
    if (!hasLoadedOnceRef.current) return;
    if (!lastGoalChange) return;
    void loadGoals();
  }, [lastGoalChange, loadGoals]);

  // U22 — refresh the week-shape projection. Cheap pure-derivation
  // endpoint; we call it once after goals load and again whenever the
  // user toggles mode / focus_priority / weights so the "This week"
  // line on the card always matches what the scheduler would do.
  const refreshWeekShape = useCallback(async () => {
    try {
      const res = await getWeekShape();
      const next: Record<string, WeeklyGoalShape> = {};
      for (const shape of res.shapes) next[shape.goal_id] = shape;
      setWeekShapeByGoal(next);
    } catch {
      // Non-fatal — the card just won't show the summary line.
    }
  }, []);

  useEffect(() => {
    if (goals.length === 0) return;
    void refreshWeekShape();
  }, [goals, refreshWeekShape]);

  useEffect(() => {
    if (!milestoneUndoNotice) return;
    const timeoutId = window.setTimeout(() => setMilestoneUndoNotice(null), 5200);
    return () => window.clearTimeout(timeoutId);
  }, [milestoneUndoNotice]);

  useEffect(() => {
    if (!showStudio) return;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowStudio(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showStudio]);

  const trackedMilestones = goals.reduce((sum, goal) => sum + goal.milestones.length, 0);
  const avgProgress = goals.length
    ? Math.round((goals.reduce((sum, goal) => sum + goal.progress_pct, 0) / goals.length) * 100)
    : 0;
  const driftGoalById = new Map(
    (goalDriftSummary?.top_drifting_goals ?? []).map((goal) => [goal.id, goal])
  );
  const {
    visibleRecoveryGoals,
    visibleSteadyGoals,
    hiddenRecoveryCount,
    hiddenSteadyCount,
    hiddenActiveCount,
  } = buildFocusedGoalGroups({
    goals,
    goalDriftSummary,
    focusGoalId: goalFocusTarget?.goalId ?? null,
    showFullInventory: filter !== "active" || showFullInventory,
  });
  const inFocusCount = filter === "active" ? visibleRecoveryGoals.length + visibleSteadyGoals.length : goals.length;

  type PaginatedGoalEntry = { goal: GoalItem; group: "recovery" | "steady" | "all" };
  const paginatedGoals = useMemo<PaginatedGoalEntry[]>(
    () =>
      filter === "active"
        ? [
            ...visibleRecoveryGoals.map((goal) => ({ goal, group: "recovery" as const })),
            ...visibleSteadyGoals.map((goal) => ({ goal, group: "steady" as const })),
          ]
        : goals.map((goal) => ({ goal, group: "all" as const })),
    [filter, visibleRecoveryGoals, visibleSteadyGoals, goals]
  );
  const [activeGoalId, setActiveGoalId] = useState<string | null>(null);
  const { setFocusedGoal } = useOverlayManager();
  // WIP-1 § 8-7 U5: publish the currently-expanded goal id so the chat
  // overlay can ground its next request against it. Cleared when no goal
  // is active, paginatedGoals empties, or this screen unmounts.
  useEffect(() => {
    setFocusedGoal(activeGoalId);
    return () => {
      setFocusedGoal(null);
    };
  }, [activeGoalId, setFocusedGoal]);
  useEffect(() => {
    if (paginatedGoals.length === 0) {
      if (activeGoalId !== null) setActiveGoalId(null);
      return;
    }
    if (!activeGoalId || !paginatedGoals.some((e) => e.goal.id === activeGoalId)) {
      setActiveGoalId(paginatedGoals[0].goal.id);
    }
  }, [paginatedGoals, activeGoalId]);
  useEffect(() => {
    if (!goalFocusTarget?.goalId) return;
    if (paginatedGoals.some((e) => e.goal.id === goalFocusTarget.goalId)) {
      setActiveGoalId(goalFocusTarget.goalId);
    }
  }, [goalFocusTarget?.goalId, paginatedGoals]);
  useEffect(() => {
    if (activeChatReveal?.surface !== "goals") return;
    if (paginatedGoals.some((e) => e.goal.id === activeChatReveal.id)) {
      setActiveGoalId(activeChatReveal.id);
    }
  }, [activeChatReveal, paginatedGoals]);
  const activeGoalIndex = Math.max(
    0,
    paginatedGoals.findIndex((e) => e.goal.id === activeGoalId)
  );
  const activeGoalEntry = paginatedGoals[activeGoalIndex] ?? null;
  const chatRevealGoalsKey = useMemo(
    () => [activeGoalEntry?.goal.id ?? "", ...paginatedGoals.map((entry) => entry.goal.id)].join("|"),
    [activeGoalEntry?.goal.id, paginatedGoals],
  );
  useChatRevealHighlight("goals", chatRevealGoalsKey);
  const setActiveGoalIndex = useCallback(
    (updater: number | ((i: number) => number)) => {
      const nextIndex = typeof updater === "function" ? updater(activeGoalIndex) : updater;
      const clamped = Math.max(0, Math.min(paginatedGoals.length - 1, nextIndex));
      const target = paginatedGoals[clamped];
      if (target) setActiveGoalId(target.goal.id);
    },
    [activeGoalIndex, paginatedGoals]
  );

  const normalize = (value: string) => value.trim().toLowerCase();

  const applyGoalToVisibleState = useCallback(
    (updatedGoal: GoalItem) => {
      setGoals((current) => {
        const withoutGoal = current.filter((goal) => goal.id !== updatedGoal.id);

        if (filter === "completed" && updatedGoal.status !== "completed") {
          return withoutGoal;
        }

        if (filter === "active" && updatedGoal.status === "completed") {
          return withoutGoal;
        }

        const next = [updatedGoal, ...withoutGoal];
        next.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        return next;
      });
    },
    [filter]
  );

  const setCardMessage = (goalId: string, message: string) => {
    setGoalActionMessage((current) => ({ ...current, [goalId]: message }));
  };

  const ensureInsight = async (goalId: string) => {
    if (goalInsights[goalId]) return goalInsights[goalId];
    const insight = await getGoalAnalysis(goalId);
    setGoalInsights((current) => ({ ...current, [goalId]: insight }));
    return insight;
  };

  const scrollGoalIntoView = useCallback((goalId: string) => {
    window.requestAnimationFrame(() => {
      goalCardRefs.current[goalId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, []);

  const openGoalRecoveryPanel = useCallback(
    async (goalId: string) => {
      setActivePanels((current) => ({
        ...current,
        [goalId]: "judge",
      }));

      if (goalInsights[goalId]) return;

      try {
        setLoadingGoalId(goalId);
        const insight = await getGoalAnalysis(goalId);
        setGoalInsights((current) => ({ ...current, [goalId]: insight }));
      } catch {
        // ignore recovery-load failures for now
      } finally {
        setLoadingGoalId(null);
      }
    },
    [goalInsights]
  );

  const buildGoalRecoveryTarget = useCallback(
    (goal: GoalItem | GoalDriftGoal) => {
      const recoveryNote = "recovery_note" in goal ? goal.recovery_note ?? null : null;
      const cleanGoalTitle = sanitizeSurfaceTitle(goal.title);
      return {
        goalId: goal.id,
        goalTitle: cleanGoalTitle,
        headline: `${cleanGoalTitle} is drifting`,
        message:
          (recoveryNote ? sanitizeSurfaceTitle(recoveryNote.replace(goal.title, cleanGoalTitle)) : null) ??
          "This goal has started losing traction. Repair the next step before protecting more time for it.",
        nextStep: goalDriftSummary?.next_step ? sanitizeSurfaceTitle(goalDriftSummary.next_step) : null,
        source: "growth" as const,
      };
    },
    [goalDriftSummary]
  );

  const focusRecoveryGoal = useCallback(
    (goal: GoalItem | GoalDriftGoal) => {
      setGoalFocusTarget(buildGoalRecoveryTarget(goal));
      scrollGoalIntoView(goal.id);
      void openGoalRecoveryPanel(goal.id);
    },
    [buildGoalRecoveryTarget, openGoalRecoveryPanel, scrollGoalIntoView, setGoalFocusTarget]
  );

  const planRecoveryGoal = useCallback(
    (goal: GoalItem | GoalDriftGoal) => {
      setGoalFocusTarget(buildGoalRecoveryTarget(goal));
      onNavigate("schedule-plan");
    },
    [buildGoalRecoveryTarget, onNavigate, setGoalFocusTarget]
  );

  useEffect(() => {
    if (filter !== "active") {
      setShowFullInventory(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!goalFocusTarget) {
      lastAutoFocusedGoalId.current = null;
      return;
    }
    if (filter !== "active") {
      setFilter("active");
      return;
    }
    const matchingGoal = goals.find((goal) => goal.id === goalFocusTarget.goalId);
    if (!matchingGoal) return;
    if (lastAutoFocusedGoalId.current === goalFocusTarget.goalId) return;

    lastAutoFocusedGoalId.current = goalFocusTarget.goalId;
    scrollGoalIntoView(goalFocusTarget.goalId);
    void openGoalRecoveryPanel(goalFocusTarget.goalId);
  }, [filter, goalFocusTarget, goals, openGoalRecoveryPanel, scrollGoalIntoView]);

  useEffect(() => {
    const revealTarget = crossSurfaceConfirmation?.revealTarget;
    if (!revealTarget || crossSurfaceConfirmation.revealNonce <= 0) return;
    if (revealTarget.surface !== "goals" || revealTarget.kind !== "goal_focus") return;

    if (filter !== "active") {
      setFilter("active");
      return;
    }

    const matchedGoal = goals.find((goal) => goal.id === revealTarget.goalId);
    if (!matchedGoal) return;

    if (goalFocusTarget?.goalId !== revealTarget.goalId) {
      setGoalFocusTarget(buildGoalRecoveryTarget(matchedGoal));
      return;
    }

    scrollGoalIntoView(revealTarget.goalId);
    void openGoalRecoveryPanel(revealTarget.goalId);
  }, [
    buildGoalRecoveryTarget,
    crossSurfaceConfirmation,
    filter,
    goalFocusTarget?.goalId,
    goals,
    openGoalRecoveryPanel,
    scrollGoalIntoView,
    setGoalFocusTarget,
  ]);

  const refreshGoalContext = async (goalId: string) => {
    await loadGoals();
    try {
      const insight = await getGoalAnalysis(goalId);
      setGoalInsights((current) => ({ ...current, [goalId]: insight }));
    } catch {
      // ignore refresh failures
    }
  };

  const createGoalTaskFromStep = async (
    goal: GoalItem,
    title: string,
    description: string,
    priority: number = 2
  ) => {
    return createTask({
      title,
      description,
      goal_id: goal.id,
      priority,
      energy_level: "medium",
      estimated_minutes: 45,
      tags: goal.category ? [goal.category, "goal-roadmap"] : ["goal-roadmap"],
    });
  };

  const buildGoalRoadmapStepConfirmation = (
    goal: GoalItem,
    task: TaskItem,
    placement: Awaited<ReturnType<typeof getTodayPlacement>> | null
  ) => {
    const cleanGoalTitle = sanitizeSurfaceTitle(goal.title);
    const cleanTaskTitle = sanitizeSurfaceTitle(task.title);
    const placementConfirmation = buildPlacementConfirmation(
      placement,
      {
        restart: `Made ${cleanTaskTitle} the restart step for ${cleanGoalTitle}.`,
        queue: `Added ${cleanTaskTitle} to Today’s queue for ${cleanGoalTitle}.`,
        plan: `Placed ${cleanTaskTitle} into Plan for ${cleanGoalTitle}.`,
        fallback: `Added ${cleanTaskTitle} as the next task for ${cleanGoalTitle}.`,
      },
      {
        targetKind: "task",
        targetId: task.id,
        owningService: "goals",
      }
    );

    return {
      state: "committed" as const,
      ...placementConfirmation,
    };
  };

  const buildGoalTimeboxConfirmation = (
    goal: GoalItem,
    plan: AutoPlanResponse,
    createdTask: boolean
  ) => {
    const cleanGoalTitle = sanitizeSurfaceTitle(goal.title);
    const focusBlock =
      (plan.focus_block_id
        ? plan.blocks.find((block) => block.id === plan.focus_block_id) ?? null
        : null) ??
      plan.blocks.find((block) => block.goal_id === goal.id) ??
      null;

    if (!focusBlock) {
      return {
        state: "info" as const,
        message: createdTask
          ? `Queued the next step for ${cleanGoalTitle} and rebuilt Plan around it.`
          : `Rebuilt Plan around the work already linked to ${cleanGoalTitle}.`,
        ctaLabel: "Open plan",
        ctaTab: "schedule-plan" as const,
      };
    }

    return {
      state: "committed" as const,
      message: createdTask
        ? `Placed a restart block for ${cleanGoalTitle} into Plan and queued the next step.`
        : `Placed a restart block for ${cleanGoalTitle} into Plan.`,
      ctaLabel: "Reveal item",
      ctaTab: "schedule-plan" as const,
      revealTarget: {
        surface: "schedule-plan" as const,
        kind: "schedule_block" as const,
        blockId: focusBlock.id,
        planDate: plan.date,
      },
      truth: {
        targetKind: "schedule_block",
        targetId: focusBlock.id,
        destinationLayer: "plan",
        owningService: "schedule",
      },
    };
  };

  const handleGoalPanel = async (goalId: string, panel: "analysis" | "roadmap" | "judge") => {
    const isClosing = activePanels[goalId] === panel;
    setActivePanels((current) => ({
      ...current,
      [goalId]: isClosing ? null : panel,
    }));

    if (isClosing || goalInsights[goalId]) return;

    try {
      setLoadingGoalId(goalId);
      const insight = await getGoalAnalysis(goalId);
      setGoalInsights((current) => ({ ...current, [goalId]: insight }));
    } catch {
      // ignore for now; the panel simply stays empty
    } finally {
      setLoadingGoalId(null);
    }
  };

  const handleApplyRoadmap = async (goal: GoalItem) => {
    const loadingKey = `${goal.id}:apply`;
    setGoalActionLoading(loadingKey);
    setCardMessage(goal.id, "");
    try {
      const insight = await ensureInsight(goal.id);
      const milestoneTitles = new Set(goal.milestones.map((item) => normalize(item.title)));
      let addedMilestones = 0;

      for (const step of insight.roadmap) {
        if (milestoneTitles.has(normalize(step.title))) continue;
        await addGoalMilestone(goal.id, step.title);
        milestoneTitles.add(normalize(step.title));
        addedMilestones += 1;
      }

      let createdTask = false;
      if (insight.linked_tasks === 0) {
        const nextStep = insight.roadmap.find((step) => step.status !== "done");
        if (nextStep) {
          await createGoalTaskFromStep(goal, nextStep.title, nextStep.reason, nextStep.status === "in_progress" ? 3 : 2);
          createdTask = true;
        }
      }

      await refreshGoalContext(goal.id);
      // BUG_EVIDENCE_PACKET_2026-05-25 B-GOAL-01: previously the
      // card-level message was the only signal of success. The modal
      // stayed open and the visible milestone count was hidden behind
      // it, so users reported the save "feels stuck". Refresh the
      // full goal list (so milestone counts update on the card),
      // close the modal, and notify the goal-change store so any
      // dependent surfaces re-fetch too.
      await loadGoals();
      const updates: string[] = [];
      if (addedMilestones > 0) updates.push(`${addedMilestones} roadmap step${addedMilestones !== 1 ? "s were" : " was"} added as milestones`);
      if (createdTask) updates.push("the best next step was added to your queue");
      setCardMessage(goal.id, updates.length > 0 ? `${updates.join(" and ")}.` : "The roadmap is already reflected in this goal.");
      if (roadmapModalGoalId === goal.id) {
        setRoadmapModalGoalId(null);
      }
    } catch (err) {
      setCardMessage(goal.id, err instanceof Error ? err.message : "Failed to apply roadmap");
    } finally {
      setGoalActionLoading(null);
    }
  };

  const handleStrengthenPlan = async (goal: GoalItem) => {
    const loadingKey = `${goal.id}:strengthen`;
    setGoalActionLoading(loadingKey);
    setCardMessage(goal.id, "");
    try {
      const plan = await getGoalStrengthenPlan(goal.id);
      const updates = Object.fromEntries(
        Object.entries(plan.updates).filter(([, value]) => value != null && value !== "")
      );

      if (Object.keys(updates).length > 0) {
        await updateGoal(goal.id, updates);
      }

      let addedMilestones = 0;
      for (const milestone of plan.new_milestones) {
        await addGoalMilestone(goal.id, milestone);
        addedMilestones += 1;
      }

      let createdTask = false;
      if (plan.starter_task) {
        await createTask({
          title: plan.starter_task.title,
          description: plan.starter_task.description,
          goal_id: goal.id,
          priority: plan.starter_task.priority,
          energy_level: plan.starter_task.energy_level,
          estimated_minutes: plan.starter_task.estimated_minutes,
          tags: goal.category ? [goal.category, "goal-starter"] : ["goal-starter"],
        });
        createdTask = true;
      }

      await refreshGoalContext(goal.id);

      const changes: string[] = [];
      if (updates.target_date) changes.push("added a target date");
      if (updates.description) changes.push("rewrote the goal description");
      if (updates.motivation) changes.push("filled in the motivation");
      if (addedMilestones > 0) changes.push("added missing milestones");
      if (createdTask) changes.push("queued the next execution step");
      const changeSummary = changes.length > 0
        ? `AI strengthened this goal: ${changes.join(", ")}.`
        : "This goal already has the main structure in place.";
      setCardMessage(goal.id, `${changeSummary} ${plan.reasoning}`);
    } catch (err) {
      setCardMessage(goal.id, err instanceof Error ? err.message : "Failed to strengthen goal");
    } finally {
      setGoalActionLoading(null);
    }
  };

  const handleTimeboxGoal = async (goal: GoalItem) => {
    const loadingKey = `${goal.id}:timebox`;
    setGoalActionLoading(loadingKey);
    setCardMessage(goal.id, "");
    try {
      const insight = await ensureInsight(goal.id);
      let createdTask = false;

      if (insight.linked_tasks === 0) {
        const nextStep = insight.roadmap.find((step) => step.status !== "done");
        if (nextStep) {
          await createGoalTaskFromStep(goal, nextStep.title, nextStep.reason, nextStep.status === "in_progress" ? 3 : 2);
          createdTask = true;
        }
      }

      const plan = await generateSchedule();
      await refreshGoalContext(goal.id);
      showCrossSurfaceConfirmation(buildGoalTimeboxConfirmation(goal, plan, createdTask));
      setCardMessage(
        goal.id,
        createdTask
          ? "Added the next goal step and rebuilt today’s timetable around it."
          : "Rebuilt today’s timetable around the work already linked to this goal."
      );
      onNavigate("schedule");
    } catch (err) {
      setCardMessage(goal.id, err instanceof Error ? err.message : "Failed to timebox goal");
    } finally {
      setGoalActionLoading(null);
    }
  };

  const handleUpdateGoalWeight = useCallback(
    async (
      goalId: string,
      field: "importance_weight" | "willingness_weight",
      value: number
    ) => {
      const goal = goals.find((item) => item.id === goalId);
      if (!goal) return;

      const loadingKey = `${goalId}:${field}`;
      setGoalWeightLoading(loadingKey);
      setCardMessage(goalId, "");
      setGoals((current) =>
        current.map((item) =>
          item.id === goalId
            ? {
                ...item,
                [field]: value,
              }
            : item
        )
      );

      try {
        const updatedGoal = await updateGoal(goalId, { [field]: value });
        applyGoalToVisibleState(updatedGoal);
        const fieldLabel =
          field === "importance_weight"
            ? `Importance is now ${goalWeightLabel("importance", updatedGoal.importance_weight).toLowerCase()}`
            : `Willingness is now ${goalWeightLabel("willingness", updatedGoal.willingness_weight).toLowerCase()}`;
        setCardMessage(
          goalId,
          `${fieldLabel}. Plan will use this when deciding how much time to protect and how heavy the entry block should be.`
        );
      } catch (err) {
        setGoals((current) =>
          current.map((item) =>
            item.id === goalId
              ? {
                  ...item,
                  [field]: goal[field],
                }
              : item
          )
        );
        setCardMessage(goalId, err instanceof Error ? err.message : "Failed to update weighting");
      } finally {
        setGoalWeightLoading(null);
      }
    },
    [applyGoalToVisibleState, goals]
  );

  // U22: handlers for the two new toggles. Both reuse the existing PATCH
  // endpoint (cascade for focus_priority is enforced server-side).
  const handleUpdateGoalMode = useCallback(
    async (goalId: string, mode: GoalItem["mode"]) => {
      if (!mode) return;
      const goal = goals.find((item) => item.id === goalId);
      if (!goal) return;
      const previous = goal.mode ?? "project";
      setCardMessage(goalId, "");
      setGoals((current) =>
        current.map((item) => (item.id === goalId ? { ...item, mode } : item))
      );
      try {
        const updated = await updateGoal(goalId, { mode });
        applyGoalToVisibleState(updated);
        // Mode change reshapes the weekly plan — re-fetch so the
        // "This week" line under the toggle reflects the new template.
        void refreshWeekShape();
        const note =
          mode === "habit"
            ? "Habit mode — AI Plan will use short daily blocks anchored to your routine."
            : mode === "direction"
            ? "Direction mode — AI Plan will use opportunistic, low-protection slots."
            : "Project mode — AI Plan will protect longer deep-work sessions.";
        setCardMessage(goalId, note);
      } catch (err) {
        setGoals((current) =>
          current.map((item) =>
            item.id === goalId ? { ...item, mode: previous } : item
          )
        );
        setCardMessage(goalId, err instanceof Error ? err.message : "Failed to update mode");
      }
    },
    [applyGoalToVisibleState, goals]
  );

  const handleUpdateGoalFocusPriority = useCallback(
    async (goalId: string, focus_priority: GoalItem["focus_priority"]) => {
      if (!focus_priority) return;
      const goal = goals.find((item) => item.id === goalId);
      if (!goal) return;
      const previous = goal.focus_priority ?? "background";
      setCardMessage(goalId, "");
      setGoals((current) =>
        current.map((item) =>
          item.id === goalId ? { ...item, focus_priority } : item
        )
      );
      try {
        const updated = await updateGoal(goalId, { focus_priority });
        applyGoalToVisibleState(updated);
        // Promoting a goal changes its budget share (1.25× for primary,
        // 1.10× for secondary) — re-fetch the week shape so the "This
        // week" line updates the session count immediately.
        void refreshWeekShape();
        // The PATCH may have demoted a peer in the same area (server-side
        // cascade). Refresh peers so the next card render reflects it.
        // Cheap: we already have all goals in state.
        if (goal.category && focus_priority !== "background") {
          setGoals((current) =>
            current.map((item) => {
              if (
                item.id !== goalId &&
                item.category === goal.category &&
                item.focus_priority === focus_priority
              ) {
                return {
                  ...item,
                  focus_priority:
                    focus_priority === "primary" ? "secondary" : "background",
                };
              }
              return item;
            })
          );
        }
        const areaLabel = (goal.category || "this area")
          .charAt(0)
          .toUpperCase() + (goal.category || "this area").slice(1);
        const note =
          focus_priority === "primary"
            ? `Promoted to Primary in ${areaLabel} — gets the biggest share of weekly time.`
            : focus_priority === "secondary"
            ? `Set as Secondary in ${areaLabel} — protected, but the Primary gets more time.`
            : `Moved to Background in ${areaLabel}.`;
        setCardMessage(goalId, note);
      } catch (err) {
        setGoals((current) =>
          current.map((item) =>
            item.id === goalId ? { ...item, focus_priority: previous } : item
          )
        );
        setCardMessage(
          goalId,
          err instanceof Error ? err.message : "Failed to update focus priority"
        );
      }
    },
    [applyGoalToVisibleState, goals]
  );

  const handleStartRoadmapStep = async (goal: GoalItem, title: string, reason: string) => {
    setRoadmapStepActionLoading(`start:${title}`);
    setCardMessage(goal.id, "");
    try {
      const createdTask = await createGoalTaskFromStep(goal, title, reason, 2);
      const placement = await getTodayPlacement(createdTask.id).catch(() => null);
      const confirmation = buildGoalRoadmapStepConfirmation(goal, createdTask, placement);
      showCrossSurfaceConfirmation(confirmation);
      setCardMessage(goal.id, confirmation.message);
      onNavigate(confirmation.ctaTab ?? "present");
    } catch (err) {
      setCardMessage(goal.id, err instanceof Error ? err.message : "Failed to create the next task");
    } finally {
      setRoadmapStepActionLoading(null);
    }
  };

  const handleCompleteRoadmapStep = async (goal: GoalItem, title: string) => {
    setRoadmapStepActionLoading(`complete:${title}`);
    setCardMessage(goal.id, "");
    try {
      const hasMilestone = goal.milestones.some((milestone) => normalize(milestone.title) === normalize(title));
      if (!hasMilestone) {
        await addGoalMilestone(goal.id, title);
      }
      await completeMilestone(goal.id, title);
      await refreshGoalContext(goal.id);
      setMilestoneUndoNotice({
        goalId: goal.id,
        goalTitle: goal.title,
        milestoneTitle: title,
      });
      setCardMessage(goal.id, `Marked "${title}" complete and updated the goal progress.`);
    } catch (err) {
      setCardMessage(goal.id, err instanceof Error ? err.message : "Failed to complete roadmap step");
    } finally {
      setRoadmapStepActionLoading(null);
    }
  };

  // Track in-flight milestone API calls to avoid race conditions
  const pendingMilestoneOps = useRef(0);
  const milestoneRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // After GoalCard's inline edit completes a wholesale milestone PUT,
  // splice the fresh goal into the local list so progress %, completion
  // state, etc. reflect server truth without a full refetch.
  const handleMilestonesReplaced = (updated: GoalItem) => {
    setGoals((prev) =>
      prev.map((g) => (g.id === updated.id ? updated : g))
    );
  };

  const handleGoalArchived = (updated: GoalItem) => {
    // Goal flipped to status="abandoned". The current filter decides
    // whether the card stays visible. Active filter → drop. Abandoned
    // or All filter → keep, splice in the updated state so the menu
    // re-renders with the Restore action.
    setGoals((prev) => {
      if (filter === "abandoned" || filter === "all") {
        const exists = prev.some((g) => g.id === updated.id);
        return exists
          ? prev.map((g) => (g.id === updated.id ? updated : g))
          : [updated, ...prev];
      }
      return prev.filter((g) => g.id !== updated.id);
    });
  };

  const handleGoalRestored = (updated: GoalItem) => {
    // Goal flipped back from "abandoned" to either "active" or
    // "completed" (user's choice in the menu). Splice in if the
    // current filter still includes the new status, otherwise drop
    // it so the visible list matches the active filter.
    setGoals((prev) => {
      const matchesFilter =
        filter === "all" ||
        filter === updated.status ||
        // "active" filter shows running goals only — so restoring
        // to "completed" means it leaves this view.
        (filter === "active" && updated.status === "active") ||
        (filter === "completed" && updated.status === "completed");
      if (matchesFilter) {
        const exists = prev.some((g) => g.id === updated.id);
        return exists
          ? prev.map((g) => (g.id === updated.id ? updated : g))
          : [updated, ...prev];
      }
      return prev.filter((g) => g.id !== updated.id);
    });
  };

  const handleGoalDeleted = (goalId: string) => {
    setGoals((prev) => prev.filter((g) => g.id !== goalId));
  };

  const handleToggleMilestone = async (goalId: string, milestoneTitle: string) => {
    const goal = goals.find((item) => item.id === goalId);
    // Optimistic update — immediately show the milestone as completed
    setGoals((prev) =>
      prev.map((g) => {
        if (g.id !== goalId) return g;
        const updatedMilestones = g.milestones.map((m) =>
          m.title === milestoneTitle ? { ...m, completed: true, completed_at: new Date().toISOString() } : m
        );
        return {
          ...g,
          milestones: updatedMilestones,
          // BUG-002/008: do NOT substitute a milestone-only optimistic % here —
          // it diverges from the backend 50/50 task+milestone blend and makes the
          // bar visibly jump when the server value lands. Keep the current value
          // and reconcile from the server response below (the checkmark still
          // updates optimistically for responsiveness).
          progress_pct: g.progress_pct,
        };
      })
    );

    // Fire API call but DON'T reconcile progress_pct from individual responses
    // (concurrent responses arrive in arbitrary order and would overwrite
    // the correct optimistic value). Instead, do a full refresh after all
    // pending operations settle.
    pendingMilestoneOps.current += 1;
    try {
      // Reconcile progress_pct from the server's authoritative blended value
      // (BUG-002/008) and notify other surfaces (BUG-010).
      const updated = await completeMilestone(goalId, milestoneTitle);
      setGoals((prev) =>
        prev.map((g) =>
          g.id === goalId
            ? {
                ...g,
                progress_pct: resolveGoalProgress(
                  updated.progress_pct,
                  g.progress_pct
                ),
              }
            : g
        )
      );
      notifyGoalChange();
      if (goal) {
        setMilestoneUndoNotice({
          goalId,
          goalTitle: goal.title,
          milestoneTitle,
        });
      }
    } catch (err) {
      // Revert on failure — full refresh to get server truth
      await loadGoals();
      setCardMessage(goalId, err instanceof Error ? err.message : "Failed to complete milestone");
    } finally {
      pendingMilestoneOps.current -= 1;
      // When all in-flight ops have settled, do one final refresh
      // to reconcile with server state (debounced 500ms)
      if (milestoneRefreshTimer.current) clearTimeout(milestoneRefreshTimer.current);
      milestoneRefreshTimer.current = setTimeout(() => {
        if (pendingMilestoneOps.current === 0) {
          void loadGoals();
        }
      }, 500);
    }
  };

  const handleUndoMilestone = useCallback(async () => {
    if (!milestoneUndoNotice) return;
    const notice = milestoneUndoNotice;
    setMilestoneUndoNotice(null);
    if (milestoneRefreshTimer.current) {
      clearTimeout(milestoneRefreshTimer.current);
      milestoneRefreshTimer.current = null;
    }
    try {
      const restoredGoal = await undoMilestone(notice.goalId, notice.milestoneTitle);
      applyGoalToVisibleState(restoredGoal);
      await refreshGoalContext(notice.goalId);
      setCardMessage(notice.goalId, `Reopened "${notice.milestoneTitle}".`);
    } catch (err) {
      setCardMessage(
        notice.goalId,
        err instanceof Error ? err.message : "Could not undo that milestone"
      );
      await loadGoals();
    }
  }, [applyGoalToVisibleState, loadGoals, milestoneUndoNotice, refreshGoalContext]);

  return (
    <div className="min-w-0 px-4 pt-4 pb-20">
      {milestoneUndoNotice && (
        <div
          data-testid="milestone-undo-toast"
          className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-blue/15 bg-blue/5 px-4 py-3"
        >
          <p className="text-xs leading-relaxed text-text">
            Marked <span className="font-semibold">{sanitizeSurfaceTitle(milestoneUndoNotice.milestoneTitle)}</span> complete in{" "}
            <span className="font-semibold">{sanitizeSurfaceTitle(milestoneUndoNotice.goalTitle)}</span>.
          </p>
          <button
            data-testid="milestone-undo-button"
            onClick={() => void handleUndoMilestone()}
            className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-blue shadow-sm ring-1 ring-blue/10 transition-colors hover:bg-blue/10"
          >
            Undo
          </button>
        </div>
      )}

      {/* Decide sub-tab header — pattern matches BacklogScreen ("Library")
          so the four sub-tabs visually align. Demoted from h1 to h2
          because DecideScreen.tsx already renders `<h1>Decide</h1>` —
          duplicate h1 was both a WCAG 2.4.6 violation and the source of
          the "Goals page looks like a different layout" UX complaint. */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">
            Decisionease · Tracking
          </p>
          <h2 className="mt-1 text-2xl font-semibold text-forest">Goals</h2>
          <p className="mt-1 text-sm text-muted">Define the outcomes that matter, then turn them into roadmaps you can actually execute.</p>
        </div>
        <button
          onClick={() => setShowStudio(true)}
          className="shrink-0 px-4 py-2 bg-forest text-white text-sm rounded-2xl font-medium
                     hover:bg-forest-med transition-colors"
        >
          + New Goal
        </button>
      </div>

      {/* Filter tabs. "Archived" surfaces goals the user previously
          archived so they have a path to restore them — without this
          tab, archive becomes a one-way trip. The backend filter
          accepts the literal goal status string. */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {[
          { value: "active", label: "Active" },
          { value: "completed", label: "Completed" },
          { value: "abandoned", label: "Archived" },
          { value: "all", label: "All" },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              filter === f.value
                ? "bg-forest text-white"
                : "bg-secondary text-muted hover:bg-secondary hover:bg-secondary"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="bg-card rounded-2xl border border-border shadow-sm p-4 animate-pulse">
              <div className="h-4 bg-muted/10 rounded w-2/3 mb-3" />
              <div className="h-2 bg-muted/5 rounded w-full mb-2" />
              <div className="h-2 bg-muted/5 rounded w-1/2" />
            </div>
          ))}
        </div>
      )}

      {/* Goals paginator */}
      {!loading && goals.length > 0 && activeGoalEntry && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Previous goal"
              disabled={activeGoalIndex === 0}
              onClick={() => setActiveGoalIndex((i) => Math.max(0, i - 1))}
              className="flex h-10 w-8 shrink-0 items-center justify-center rounded-full text-text-light transition hover:bg-sage-pale disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <div className="flex-1 overflow-x-auto scrollbar-none">
              <div className="flex items-center gap-2">
                {paginatedGoals.map((entry, index) => {
                  const isActive = index === activeGoalIndex;
                  const isRecovery = entry.group === "recovery";
                  return (
                    <button
                      key={entry.goal.id}
                      type="button"
                      onClick={() => setActiveGoalIndex(index)}
                      className={`flex shrink-0 items-center gap-1.5 rounded-2xl border px-3 py-2 text-[12px] font-semibold transition ${
                        isActive
                          ? "border-forest-med bg-forest-med text-white shadow-sm"
                          : "border-border bg-cream text-text-light hover:border-sage hover:text-forest"
                      }`}
                    >
                      {isRecovery && (
                        <span className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-white" : "bg-blue"}`} />
                      )}
                      <span className="max-w-[140px] truncate">{entry.goal.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <button
              type="button"
              aria-label="Next goal"
              disabled={activeGoalIndex >= paginatedGoals.length - 1}
              onClick={() => setActiveGoalIndex((i) => Math.min(paginatedGoals.length - 1, i + 1))}
              className="flex h-10 w-8 shrink-0 items-center justify-center rounded-full text-text-light transition hover:bg-sage-pale disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>

          <div className="flex items-center justify-end text-[11px] uppercase tracking-[0.16em]">
            <span className="font-medium text-muted">
              {activeGoalIndex + 1} / {paginatedGoals.length}
            </span>
          </div>

          <div
            key={activeGoalEntry.goal.id}
            ref={(node) => {
              goalCardRefs.current[activeGoalEntry.goal.id] = node;
            }}
            data-testid={goalFocusTarget?.goalId === activeGoalEntry.goal.id ? "goal-focused-card" : undefined}
            data-reveal-id={activeGoalEntry.goal.id}
          >
            <GoalCard
              goal={activeGoalEntry.goal}
              insight={goalInsights[activeGoalEntry.goal.id]}
              activePanel={activePanels[activeGoalEntry.goal.id]}
              loading={loadingGoalId === activeGoalEntry.goal.id}
              actionLoading={goalActionLoading}
              actionMessage={goalActionMessage[activeGoalEntry.goal.id]}
              onSelectPanel={handleGoalPanel}
              onApplyRoadmap={() => void handleApplyRoadmap(activeGoalEntry.goal)}
              onStrengthenPlan={() => void handleStrengthenPlan(activeGoalEntry.goal)}
              onTimeboxGoal={() => void handleTimeboxGoal(activeGoalEntry.goal)}
              onUpdateWeight={(goalId, field, value) => void handleUpdateGoalWeight(goalId, field, value)}
              onUpdateMode={(goalId, mode) => handleUpdateGoalMode(goalId, mode)}
              onUpdateFocusPriority={(goalId, fp) =>
                handleUpdateGoalFocusPriority(goalId, fp)
              }
              peersInArea={goals.filter(
                (g) =>
                  g.id !== activeGoalEntry.goal.id &&
                  g.status === "active" &&
                  g.category === activeGoalEntry.goal.category
              )}
              weeklyShape={weekShapeByGoal[activeGoalEntry.goal.id] ?? null}
              onToggleMilestone={handleToggleMilestone}
              onMilestonesReplaced={handleMilestonesReplaced}
              onGoalArchived={handleGoalArchived}
              onGoalRestored={handleGoalRestored}
              onGoalDeleted={handleGoalDeleted}
              onOpenRoadmapModal={() => {
                void ensureInsight(activeGoalEntry.goal.id);
                setRoadmapModalGoalId(activeGoalEntry.goal.id);
              }}
              highlighted={goalFocusTarget?.goalId === activeGoalEntry.goal.id}
              weightUpdateLoading={goalWeightLoading}
            />
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && goals.length === 0 && (
        <div className="bg-card rounded-2xl border border-border shadow-sm p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-blue/10 flex items-center justify-center mx-auto mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="1.5">
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="5" />
              <circle cx="12" cy="12" r="1" fill="var(--blue)" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-text mb-2">
            {filter === "completed" ? "No completed goals yet" : "No goals yet"}
          </h3>
          <p className="text-sm text-muted max-w-xs mx-auto leading-relaxed">
            Tell me about something you want to achieve, and I&apos;ll help you break it
            into manageable milestones.
          </p>
          <button
            onClick={() => setShowStudio(true)}
            className="mt-4 px-4 py-2 bg-forest/10 text-forest text-sm rounded-xl font-medium
                       hover:bg-forest/20 transition-colors"
          >
            Set your first goal
          </button>
        </div>
      )}

      {/* What goals can do */}
      {!loading && goals.length === 0 && filter === "active" && (
        <div className="mt-6 space-y-3">
          <h2 className="text-sm font-semibold text-text-light">What the Goal Tracker does</h2>
          {[
            { icon: "&#128200;", title: "Progress tracking", desc: "Visual progress bars and milestone tracking" },
            { icon: "&#128276;", title: "Smart reminders", desc: "Gentle nudges when you haven't checked in" },
            { icon: "&#127942;", title: "Celebrations", desc: "Recognises your wins, big and small" },
          ].map((feature) => (
            <div key={feature.title} className="flex items-start gap-3 p-3 bg-card rounded-xl border border-border">
              <div className="w-9 h-9 rounded-lg bg-sage-pale/50 flex items-center justify-center flex-shrink-0">
                <span className="text-base" dangerouslySetInnerHTML={{ __html: feature.icon }} />
              </div>
              <div>
                <p className="text-sm font-medium text-text">{feature.title}</p>
                <p className="text-xs text-muted">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {showStudio && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="goal-studio-title"
          className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/30 px-4 py-6 backdrop-blur-[2px] sm:py-10 lg:left-60"
          onClick={() => setShowStudio(false)}
        >
          <div
            className="w-full max-w-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <GoalStudio
              onClose={() => setShowStudio(false)}
              onCreated={loadGoals}
              onNavigate={onNavigate}
            />
          </div>
        </div>
      )}

      {/* Roadmap Modal — dogfood round-2 fix: previously returned
          null when modalInsight wasn't loaded yet, so clicking
          "Generate roadmap" on a goal whose insight hadn't been
          prefetched looked like a silent no-op (the analysis API
          is LLM-backed and takes multi-second). Now: as soon as
          roadmapModalGoalId is set, render a loading shell so the
          user sees the click landed. The full RoadmapModal takes
          over once modalInsight arrives. */}
      {roadmapModalGoalId && (() => {
        const modalGoal = goals.find((g) => g.id === roadmapModalGoalId);
        if (!modalGoal) return null;
        const modalInsight = goalInsights[roadmapModalGoalId];
        if (!modalInsight) {
          return (
            <div
              role="dialog"
              aria-modal="true"
              aria-busy="true"
              aria-label="Loading roadmap"
              data-testid="roadmap-modal-loading"
              className="fixed inset-0 z-[120] flex items-center justify-center px-4"
              style={{ backgroundColor: "rgba(40,28,18,0.32)" }}
              onClick={() => setRoadmapModalGoalId(null)}
            >
              <div
                className="relative w-full max-w-lg rounded-[14px] bg-[#FFF8F0] shadow-2xl px-7 py-8"
                onClick={(event) => event.stopPropagation()}
              >
                <p className="text-[10.5px] font-bold uppercase tracking-[1.4px] text-amber">
                  Generating roadmap
                </p>
                <h3 className="mt-1 font-serif text-[22px] font-medium tracking-[-0.3px] text-text">
                  {modalGoal.title}
                </h3>
                <p className="mt-2 text-[13px] leading-relaxed text-text-light">
                  Analysing this goal and drafting a roadmap. This usually takes
                  a few seconds…
                </p>
                <div className="mt-5 flex items-center gap-3">
                  <div
                    className="h-2 w-2 animate-pulse rounded-full bg-forest-med"
                    aria-hidden="true"
                  />
                  <span className="text-[11.5px] text-text-light">
                    Thinking…
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setRoadmapModalGoalId(null)}
                  className="mt-6 rounded-full border border-border px-4 py-1.5 text-[12px] font-semibold text-text-light transition-colors hover:bg-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          );
        }
        return (
          <RoadmapModal
            goal={modalGoal}
            insight={modalInsight}
            onClose={() => setRoadmapModalGoalId(null)}
            onApplyRoadmap={() => void handleApplyRoadmap(modalGoal)}
            applyingRoadmap={goalActionLoading === `${roadmapModalGoalId}:apply`}
            onStartStep={(step) => void handleStartRoadmapStep(modalGoal, step.title, step.reason)}
            onCompleteStep={(step) => void handleCompleteRoadmapStep(modalGoal, step.title)}
            stepActionLoading={roadmapStepActionLoading}
          />
        );
      })()}
    </div>
  );
}
